import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_LEARNING_ASSIGNMENT_STATUSES,
  HR_LEARNING_COMPLETION_STATUSES,
  HR_SKILL_STATUSES,
  HrCertificateRequirement,
  HrCourseCompletion,
  HrEmployee,
  HrEmployeeSkill,
  HrLearningAssignment,
  HrLearningCourse,
  HrLearningDashboard,
  HrReadinessLearningImpact,
  HrSkill
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

interface SelectOption<T = string | number | boolean> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-hr-learning',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-learning.component.html',
  styleUrls: ['./hr-learning.component.scss']
})
export class HrLearningComponent implements OnInit {
  activeListTab = 'courses';
  activeDialog?: 'course' | 'skill' | 'employeeSkill' | 'assignment' | 'completion' | 'requirement';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  readonly canManage = this.permissionService.hasPermission(app_permissions.hr.learning.manage);
  readonly canAssign = this.permissionService.hasPermission(app_permissions.hr.learning.assign);
  readonly canComplete = this.permissionService.hasPermission(app_permissions.hr.learning.complete);
  readonly yesNoOptions: SelectOption<boolean>[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];
  readonly assignmentStatusOptions = HR_LEARNING_ASSIGNMENT_STATUSES.map(value => ({ label: value, value }));
  readonly completionStatusOptions = HR_LEARNING_COMPLETION_STATUSES.map(value => ({ label: value, value }));
  readonly skillStatusOptions = HR_SKILL_STATUSES.map(value => ({ label: value, value }));
  workerCategoryOptions: SelectOption<string>[] = [];

  dashboard?: HrLearningDashboard;
  employees: HrEmployee[] = [];
  skills: HrSkill[] = [];
  employeeSkills: HrEmployeeSkill[] = [];
  courses: HrLearningCourse[] = [];
  assignments: HrLearningAssignment[] = [];
  completions: HrCourseCompletion[] = [];
  requirements: HrCertificateRequirement[] = [];
  impacts: HrReadinessLearningImpact[] = [];

  skillForm: HrSkill = { active: true, requiredForReadiness: false };
  courseForm: HrLearningCourse = { active: true, requiredForReadiness: true, deliveryMode: 'CLASSROOM' };
  employeeSkillForm: HrEmployeeSkill = { skillStatus: 'ACTIVE' };
  assignmentForm: HrLearningAssignment = { assignmentStatus: 'ASSIGNED' };
  completionForm: HrCourseCompletion = { completionStatus: 'COMPLETED' };
  requirementForm: HrCertificateRequirement = { active: true, requiredForReadiness: true, renewalWindowDays: 30 };
  completionImportJson = '';

  ngOnInit(): void {
    this.loadWorkerCategories();
    this.load();
  }

  private loadWorkerCategories(): void {
    this.hrService.workerCategories().subscribe(response => {
      this.workerCategoryOptions = (response.data ?? []).map(item => ({
        label: item.categoryName || item.categoryCode || '', value: item.categoryCode || ''
      }));
    });
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Skills', value: this.dashboard?.skills || 0, tone: 'info' },
      { label: 'Courses', value: this.dashboard?.courses || 0, tone: 'success' },
      { label: 'Open Assignments', value: this.dashboard?.openAssignments || 0, tone: 'warn' },
      { label: 'Completions', value: this.dashboard?.completions || 0, tone: 'success' },
      { label: 'Readiness Blocks', value: this.impacts.length, tone: this.impacts.length ? 'danger' : 'success' }
    ];
  }

  get employeeOptions(): SelectOption<number>[] {
    return this.employees.map(employee => ({
      label: `${employee.employeeNumber || employee.employeeId} - ${employee.displayName}`,
      value: employee.employeeId!
    }));
  }

  get skillOptions(): SelectOption<number>[] {
    return this.skills.map(skill => ({ label: `${skill.skillCode} - ${skill.skillName}`, value: skill.skillId! }));
  }

  get courseOptions(): SelectOption<number>[] {
    return this.courses.map(course => ({ label: `${course.courseCode} - ${course.courseName}`, value: course.courseId! }));
  }

  get assignmentOptions(): SelectOption<number>[] {
    return this.assignments.map(assignment => ({
      label: `${assignment.employeeName || assignment.employeeId} - ${assignment.courseName || assignment.courseId}`,
      value: assignment.assignmentId!
    }));
  }

  load(): void {
    forkJoin({
      dashboard: this.hrService.learningDashboard(),
      employees: this.hrService.employees({ page: 0, pageSize: 500 }),
      skills: this.hrService.learningSkills(),
      employeeSkills: this.hrService.employeeSkills(),
      courses: this.hrService.learningCourses(),
      assignments: this.hrService.learningAssignments(),
      completions: this.hrService.learningCompletions(),
      requirements: this.hrService.certificateRequirements(),
      impacts: this.hrService.learningReadinessImpacts()
    }).subscribe({
      next: result => {
        this.dashboard = result.dashboard.data;
        this.employees = result.employees.items || [];
        this.skills = result.skills.data || [];
        this.employeeSkills = result.employeeSkills.data || [];
        this.courses = result.courses.data || [];
        this.assignments = result.assignments.data || [];
        this.completions = result.completions.data || [];
        this.requirements = result.requirements.data || [];
        this.impacts = result.impacts.data || [];
      },
      error: () => this.toast('error', 'Unable to load learning workspace.')
    });
  }

  saveSkill(): void {
    if (!this.canManage) return;
    this.hrService.saveLearningSkill(this.skillForm).subscribe({
      next: () => {
        this.skillForm = { active: true, requiredForReadiness: false };
        this.activeDialog = undefined;
        this.toast('success', 'Skill saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save skill.')
    });
  }

  saveCourse(): void {
    if (!this.canManage) return;
    this.hrService.saveLearningCourse(this.courseForm).subscribe({
      next: () => {
        this.courseForm = { active: true, requiredForReadiness: true, deliveryMode: 'CLASSROOM' };
        this.activeDialog = undefined;
        this.toast('success', 'Course saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save course.')
    });
  }

  seedDefaults(): void {
    if (!this.canManage) return;
    this.hrService.seedLearningDefaults().subscribe({
      next: result => {
        this.toast('success', `${result.data?.length || 0} logistics defaults available.`);
        this.load();
      },
      error: () => this.toast('error', 'Unable to seed logistics defaults.')
    });
  }

  saveEmployeeSkill(): void {
    if (!this.canManage) return;
    this.hrService.saveEmployeeSkill(this.employeeSkillForm).subscribe({
      next: () => {
        this.employeeSkillForm = { skillStatus: 'ACTIVE' };
        this.activeDialog = undefined;
        this.toast('success', 'Employee skill saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save employee skill.')
    });
  }

  saveAssignment(): void {
    if (!this.canAssign) return;
    this.hrService.saveLearningAssignment(this.assignmentForm).subscribe({
      next: () => {
        this.assignmentForm = { assignmentStatus: 'ASSIGNED' };
        this.activeDialog = undefined;
        this.toast('success', 'Training assignment saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save assignment.')
    });
  }

  saveCompletion(): void {
    if (!this.canComplete) return;
    this.hrService.saveLearningCompletion(this.completionForm).subscribe({
      next: () => {
        this.completionForm = { completionStatus: 'COMPLETED' };
        this.activeDialog = undefined;
        this.toast('success', 'Completion recorded.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to record completion.')
    });
  }

  importCompletions(): void {
    if (!this.canComplete) return;
    try {
      const completions = JSON.parse(this.completionImportJson || '[]') as HrCourseCompletion[];
      this.hrService.importLearningCompletions({ completions }).subscribe({
        next: result => {
          this.toast('success', `${result.data?.imported || 0} completions imported.`);
          this.completionImportJson = '';
          this.load();
        },
        error: () => this.toast('error', 'Unable to import completions.')
      });
    } catch {
      this.toast('error', 'Import payload must be a JSON array of completion records.');
    }
  }

  saveRequirement(): void {
    if (!this.canManage) return;
    this.hrService.saveCertificateRequirement(this.requirementForm).subscribe({
      next: () => {
        this.requirementForm = { active: true, requiredForReadiness: true, renewalWindowDays: 30 };
        this.activeDialog = undefined;
        this.toast('success', 'Certificate requirement saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save certificate requirement.')
    });
  }

  severity(status?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' {
    if (['COMPLETED', 'ACTIVE'].includes(status || '')) return 'success';
    if (['EXPIRED', 'OVERDUE', 'MISSING', 'FAILED'].includes(status || '')) return 'danger';
    if (['ASSIGNED', 'IN_PROGRESS'].includes(status || '')) return 'warn';
    return 'info';
  }

  openActionDialog(dialog: NonNullable<HrLearningComponent['activeDialog']>): void {
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: NonNullable<HrLearningComponent['activeDialog']>): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: NonNullable<HrLearningComponent['activeDialog']>, visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  private toast(severity: 'success' | 'error', detail: string): void {
    this.messageService.add({ severity, summary: 'HR Learning', detail });
  }
}
